import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import { useCurrentUser } from 'services/repositories/user/user';
import {
  ConfirmationModalIds,
  isForeverHiddenConfirmationModal,
} from 'support/helpers/confirmationModals/confirmationModals';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { DocumentTypeWithVirtual, MessageV2DTO, TransactionsRoutes } from 'support/types';
import { SendConfirmationModal } from 'pages/Transactions/components/CreateDocument/components/SendConfirmationModal/SendConfirmationModal';
import { Alert, AlertSection } from 'components/Display/Alert';
import { Translate } from 'components/Translate/Translate';

import { useAlertConfig } from './hooks/useAlertConfig';
import { usePendingReviewActions } from './hooks/usePendingReviewActions';
import { DeliverNewVersionConfirmationModal } from './components/DeliverNewVersionConfirmationModal/DeliverNewVersionConfirmationModal';
import { PendingReviewComment } from './components/PendingReviewComment/PendingReviewComment';
import { ReviewApprovalConfirmationModal } from './components/ReviewApprovalConfirmationModal/ReviewApprovalConfirmationModal';
import { SkipDeliveryConfirmationModal } from './components/SkipDeliveryConfirmationModal/SkipDeliveryConfirmationModal';

type ReviewBannerProps = {
  message: MessageV2DTO;
};

export const ReviewBanner = ({ message }: ReviewBannerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const review = message.review;
  const [comment, setComment] = useState(review?.comments[0]);
  const [isEditingComment, setEditingComment] = useState(false);
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);
  const [showDeliverNewVersionConfirmation, setShowDeliverNewVersionConfirmation] = useState(false);
  const [showSkipDeliveryConfirmation, setShowSkipDeliveryConfirmation] = useState(false);
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);

  const { handleApprove, handleComment, handleDeleteComment, handleCancelEdit, isApprovingMessage, isMessageApproved } =
    usePendingReviewActions({
      reviewId: review?.id,
      onCommentChange: setComment,
      onEditingChange: setEditingComment,
      currentComment: comment,
      virtualDocumentType: message.type,
    });

  const alertConfig = useAlertConfig(review, message);

  const handleApproveClick = () => {
    if (isForeverHiddenConfirmationModal(ConfirmationModalIds.ReviewApprovalConfirmation, currentUser?.id || '')) {
      handleApprove();
      return;
    }
    setShowApprovalConfirmation(true);
  };

  const handleConfirmApproval = () => {
    setShowApprovalConfirmation(false);
    handleApprove();
  };

  const handleCancelApproval = () => {
    setShowApprovalConfirmation(false);
  };

  const handleDeliverNewVersionClick = () => {
    setShowDeliverNewVersionConfirmation(true);
  };

  const handleConfirmDeliverNewVersion = () => {
    setShowDeliverNewVersionConfirmation(false);
    handleApprove('deliver');
  };

  const handleCancelDeliverNewVersion = () => {
    setShowDeliverNewVersionConfirmation(false);
  };

  const handleSkipDeliveryClick = () => {
    setShowSkipDeliveryConfirmation(true);
  };

  const handleConfirmSkipDelivery = () => {
    setShowSkipDeliveryConfirmation(false);
    handleApprove('skip_delivery');
  };

  const handleCancelSkipDelivery = () => {
    setShowSkipDeliveryConfirmation(false);
  };

  useEffect(() => {
    // Since we are copying the props, we need to ensure that if the message is updated  we also update the comment state
    setComment(review?.comments[0]);
  }, [review]);

  if (isMessageApproved || (review?.completed_by && review?.completed_at)) {
    return (
      <Alert severity="success" withoutIcon>
        <AlertSection
          severity="success"
          title={t('transactions:show.sections.alert.review.approved.title', {
            documentType: t(`common:messageTypes.singular.${message.type}`),
          })}
          className="flex flex-col gap-2"
        >
          <Translate
            i18nKey="transactions:show.sections.alert.review.approved.description"
            values={{
              approvedBy: review?.completed_by?.name ?? currentUser?.name,
              approvedAt: formatDayAndTime(review?.completed_at ?? new Date()),
            }}
          />
        </AlertSection>
      </Alert>
    );
  }

  if (!review || !currentUser) {
    return null;
  }

  const getActions = () => {
    if (isEditingComment) {
      return {
        primary: {
          children: t('transactions:show.sections.alert.review.comment.post'),
          onClick: () => handleComment(comment?.content || ''),
          disabled: !comment?.content,
          analyticsId: 'webedi:pending-review-banner-post-comment',
        },
        secondary: {
          children: t('common:cancel'),
          onClick: () => handleCancelEdit(review.comments[0]),
          analyticsId: 'webedi:pending-review-banner-cancel-edit-comment',
        },
      };
    }

    if (review?.type === 'document_requires_manual_review') {
      return {
        primary: {
          children: t('transactions:show.sections.alert.review.approve'),
          variant: 'primary' as const,
          onClick: () => setShowSendConfirmation(true),
          loading: isApprovingMessage,
          LeftIcon: CheckIcon,
          analyticsId: 'webedi:pending-review-banner-approve',
        },
        secondary: {
          children: t('webedi:viewDocument.editCTA'),
          onClick: () => {
            navigate(routeToPage(TransactionsRoutes.edit, { id: message.id }), { state: { keepBreadcrumbs: true } });
          },
          LeftIcon: PencilSquareIcon,
          analyticsId: 'webedi:pending-review-banner-edit',
        },
      };
    }

    if (review?.type === 'document_changed') {
      return {
        primary: {
          children: t('transactions:show.sections.alert.review.documentChanged.actions.deliverNewVersion'),
          variant: 'primary' as const,
          onClick: handleDeliverNewVersionClick,
          loading: isApprovingMessage,
          analyticsId: 'webedi:pending-review-banner-deliver-new-version',
        },
        secondary: {
          children: t('transactions:show.sections.alert.review.documentChanged.actions.skipDelivery'),
          onClick: handleSkipDeliveryClick,
          loading: isApprovingMessage,
          analyticsId: 'webedi:pending-review-banner-skip-delivery',
        },
      };
    }

    return {
      primary: {
        children: t('transactions:show.sections.alert.review.approve'),
        variant: 'primary' as const,
        onClick: handleApproveClick,
        loading: isApprovingMessage,
        LeftIcon: CheckIcon,
        analyticsId: 'webedi:pending-review-banner-approve',
      },
      secondary: comment
        ? undefined
        : {
            children: t('transactions:show.sections.alert.review.comment.add'),
            onClick: () => setEditingComment(true),
            loading: isApprovingMessage,
            analyticsId: 'webedi:pending-review-banner-add-comment',
          },
    };
  };

  return (
    <>
      <Alert
        severity={alertConfig.severity}
        withoutIcon
        actions={{
          alignment: 'bottom-left',
          ...getActions(),
        }}
      >
        <AlertSection severity={alertConfig.severity} title={alertConfig.title} className="flex flex-col gap-2">
          {alertConfig.description}
          <PendingReviewComment
            disabled={isApprovingMessage}
            isEditing={isEditingComment}
            comment={comment}
            onCommentChange={setComment}
            toggleIsEditing={() => setEditingComment(!isEditingComment)}
            onDelete={handleDeleteComment}
            currentUser={currentUser}
          />
        </AlertSection>
      </Alert>

      <ReviewApprovalConfirmationModal
        open={showApprovalConfirmation}
        onConfirm={handleConfirmApproval}
        onCancel={handleCancelApproval}
        message={message}
      />

      <DeliverNewVersionConfirmationModal
        open={showDeliverNewVersionConfirmation}
        onConfirm={handleConfirmDeliverNewVersion}
        onCancel={handleCancelDeliverNewVersion}
      />

      <SkipDeliveryConfirmationModal
        open={showSkipDeliveryConfirmation}
        onConfirm={handleConfirmSkipDelivery}
        onCancel={handleCancelSkipDelivery}
      />

      <SendConfirmationModal
        open={showSendConfirmation}
        onConfirm={() => {
          setShowSendConfirmation(false);
          handleApprove();
        }}
        onCancel={() => setShowSendConfirmation(false)}
        documentType={message.type as DocumentTypeWithVirtual}
        receiverTradePartner={message.receiver_partner ?? undefined}
      />
    </>
  );
};
